import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { FileText, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Claim {
  id: string;
  claim_type: string;
  incident_date: string;
  description: string | null;
  status: string;
  created_at: string;
}

interface ClaimsListProps {
  contactId: string;
}

export const ClaimsList = ({ contactId }: ClaimsListProps) => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClaims = async () => {
      const { data, error } = await supabase
        .from('claims')
        .select('*')
        .eq('user_id', contactId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching claims:', error);
        return;
      }

      setClaims(data || []);
      setLoading(false);
    };

    fetchClaims();
  }, [contactId]);

  const handleEdit = (claim: Claim) => {
    console.log('Edit claim:', claim);
    // Add edit functionality here
  };

  const handleDelete = async (claimId: string) => {
    try {
      const { error } = await supabase
        .from('claims')
        .delete()
        .eq('id', claimId);

      if (error) throw error;

      setClaims(claims.filter(claim => claim.id !== claimId));
    } catch (error) {
      console.error('Error deleting claim:', error);
    }
  };

  if (loading) {
    return <div className="animate-pulse bg-gray-100 h-32 rounded-lg" />;
  }

  if (claims.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Claims History</h3>
      <div className="space-y-4">
        {claims.map((claim) => (
          <Card 
            key={claim.id} 
            className="p-6 bg-white border border-[#E5DEFF] hover:border-primary transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-[#F1F0FB] rounded-lg">
                    <FileText className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary-dark">
                      {claim.claim_type}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-blue-100 text-blue-800 mt-1">
                      {claim.status}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2 mt-4">
                  <p className="text-sm text-neutral">
                    Incident Date:{" "}
                    <span className="text-primary-dark">
                      {claim.incident_date ? 
                        format(new Date(claim.incident_date), 'PPP p') : 
                        'Not specified'}
                    </span>
                  </p>
                  {claim.description && (
                    <p className="text-sm text-neutral">
                      Description:{" "}
                      <span className="text-primary-dark">
                        {claim.description}
                      </span>
                    </p>
                  )}
                  <p className="text-xs text-neutral">
                    Submitted on{" "}
                    <span className="text-primary-dark">
                      {format(new Date(claim.created_at), 'PPP')}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(claim)}
                  className="hover:bg-primary/10 hover:text-primary"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete the claim.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(claim.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};